import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth helpers
export const getToken = () => localStorage.getItem("token");
export const setToken = (token) => localStorage.setItem("token", token);
export const removeToken = () => localStorage.removeItem("token");

export const getUser = () => {
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user) : null;
};
export const setUser = (user) => localStorage.setItem("user", JSON.stringify(user));
export const removeUser = () => localStorage.removeItem("user");

// API helper with auth
export const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Criar AbortController para timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos timeout

  try {
    const response = await fetch(`${API_URL}/api${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);

    if (response.status === 401) {
      // Se é um erro de login (credenciais inválidas), não redirecionar
      const isLoginEndpoint = endpoint === "/auth/login";
      // Endpoints de verificação de senha também não devem redirecionar
      const isPasswordVerificationEndpoint = endpoint.includes("verificar-senha");
      
      if (!isLoginEndpoint && !isPasswordVerificationEndpoint) {
        // Sessão expirada - redirecionar para login
        removeToken();
        removeUser();
        window.location.href = "/login";
        throw new Error("Sessão expirada");
      }
      
      // Erro de credenciais no login ou verificação de senha - retornar mensagem apropriada
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || "Senha incorreta");
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const detail = error.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
          : detail ? JSON.stringify(detail) : "Erro na requisição";
      throw new Error(msg);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error("A requisição demorou muito. Verifique sua conexão e tente novamente.");
    }
    throw error;
  }
};

// Date formatting
export const hojeBrasil = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const horaBrasil = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("pt-BR");
};

export const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("pt-BR");
};

// Status helpers
export const getStatusInfo = (status) => {
  const statusMap = {
    em_dia: { label: "Em dia", color: "emerald", icon: "CheckCircle2" },
    vencendo: { label: "Vencendo", color: "amber", icon: "AlertTriangle" },
    vencido: { label: "Vencido", color: "red", icon: "XCircle" },
  };
  return statusMap[status] || statusMap.em_dia;
};

// Event type labels
export const getTipoEventoLabel = (tipo) => {
  const labels = {
    licença: "Licença/GVS",
    conselho_profissional: "Conselho Profissional",
    produto_controlado: "Produto Controlado",
    análise: "Análise",
    calibracao_equipamento: "Calibração de Equipamento",
    calibracao_termohigrometro: "Calibração de Termohigrômetro",
    resíduos: "Descarte de Resíduos",
    // Documentos
    documento: "Documento",
    alvara: "Alvará",
    licenca: "Licença",
    certificado: "Certificado",
    // Itens Calibráveis
    item_calibravel: "Item Calibrável",
  };
  return labels[tipo] || tipo;
};

// Periodicidade labels
export const getPeriodicidadeLabel = (periodicidade) => {
  const labels = {
    única: "Única",
    mensal: "Mensal",
    trimestral: "Trimestral",
    semestral: "Semestral",
    anual: "Anual",
    personalizada: "Personalizada",
  };
  return labels[periodicidade] || periodicidade;
};

// Categoria labels
export const getCategoriaLabel = (categoria) => {
  const labels = {
    equipamento: "Equipamento",
    termohigrometro: "Termohigrômetro",
  };
  return labels[categoria] || categoria;
};

// Setor labels
export const getSetorLabel = (setor) => {
  const labels = {
    sala_limpa: "Sala Limpa",
    estoque: "Estoque",
    laboratorio: "Laboratório",
    geladeira: "Geladeira",
    outro: "Outro",
  };
  return labels[setor] || setor;
};

// Calculate days remaining
export const getDaysRemaining = (dateString) => {
  if (!dateString) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(dateString);
  targetDate.setHours(0, 0, 0, 0);
  const diffTime = targetDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Validar CNPJ (algoritmo oficial da Receita Federal)
export const validarCNPJ = (cnpj) => {
  // Remover caracteres não numéricos
  cnpj = cnpj.replace(/[^\d]/g, '');
  
  // CNPJ deve ter 14 dígitos
  if (cnpj.length !== 14) {
    return false;
  }
  
  // CNPJs inválidos conhecidos (todos os dígitos iguais)
  if (/^(\d)\1{13}$/.test(cnpj)) {
    return false;
  }
  
  // Cálculo do primeiro dígito verificador
  let soma = 0;
  let peso = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (let i = 0; i < 12; i++) {
    soma += parseInt(cnpj[i]) * peso[i];
  }
  let resto = soma % 11;
  let digito1 = resto < 2 ? 0 : 11 - resto;
  
  if (parseInt(cnpj[12]) !== digito1) {
    return false;
  }
  
  // Cálculo do segundo dígito verificador
  soma = 0;
  peso = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (let i = 0; i < 13; i++) {
    soma += parseInt(cnpj[i]) * peso[i];
  }
  resto = soma % 11;
  let digito2 = resto < 2 ? 0 : 11 - resto;
  
  if (parseInt(cnpj[13]) !== digito2) {
    return false;
  }
  
  return true;
};
